/**
 * The entry point. It decides one thing before the app exists: is this page on the
 * app's own address?
 *
 * The app is reachable on addresses it does not live on — the hosting provider's
 * defaults. A visitor on one of those is sent to the same path on the app's address,
 * and nothing else is loaded: no sign-in, no call to the API (openspec/specs/infra). The
 * decision is canonical.mjs. The bare domain and www are the public site's (site/).
 *
 * The app is a dynamic import so that none of it runs on the wrong address. A static
 * import would be hoisted above the check and start Firebase before the redirect.
 */
import { redirectTarget } from '../canonical.mjs';

const target = redirectTarget(import.meta.env.VITE_WEB_BASE_URL, window.location);

if (target) {
  void leaveFor(target);
} else {
  void import('./main');
}

/**
 * A service worker installed on this address would go on answering it from its cached
 * shell, share target included. It has no job here any more; remove it, then go.
 * Failing to remove it must not stop the redirect.
 */
async function leaveFor(url: string): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(registrations.map((r) => r.unregister()));
  } catch {
    // Nothing to do about it, and nothing depends on it.
  }
  window.location.replace(url);
}
